import type { FC } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { IRideDto } from "@ryde/shared";

import { MuiButton } from "../../../../controls";
import { DropdownTemplate } from "../../../../dynamic-forms";
import { TableLoadingIndicator } from "../../../../grid";
import { AddStationWrapperStyled } from "../../../misc";
import {
  ButtonAddStation,
  ButtonAddStationToFixedRide,
  ButtonOpenStation,
} from "../controls";
import { RIDE_ADD_STATION_NEW_STATION } from "../parts/ride-stations-panel.constants";
import { RideAddQuickStationForm } from "./ride-add-quick-station-form";
import { useRideAddStation } from "./use-ride-add-station";

interface IRideAddStation {
  rideDto: Partial<IRideDto>;
  isRideWithDuplicatedStationsAllowed?: boolean;
  closeExpanded: () => void;
}

/* TODO: adding station without passengers will be enabled when fixed rides will be enabled for adding */
export const RideAddStation: FC<IRideAddStation> = ({
  rideDto,
  isRideWithDuplicatedStationsAllowed,
  closeExpanded,
}) => {
  const { t } = useTranslation();
  const {
    methods,
    isStationsDataLoading,
    stationsDropdownListFiltered,
    watchedStationId,
    hasPermissionEditStation,
    hasPermissionCreateStation,
    showQuickStationForm,
    selectStationFromDrawer,
    openStationCard,
    openQuickStationForm,
    handleQuickStationSave,
    handleQuickStationClose,
    handleStationAdd,
  } = useRideAddStation({ rideDto, isRideWithDuplicatedStationsAllowed });

  return (
    <FormProvider {...methods}>
      {!isStationsDataLoading && (
        <AddStationWrapperStyled>
          <DropdownTemplate
            name="stationAddId"
            size="small"
            formAttributes={{
              selectItems: stationsDropdownListFiltered,
              disabled: !stationsDropdownListFiltered?.length,
              skipDropdownItemsTranslation: true,
            }}
          />
          <ButtonOpenStation
            watchedId={watchedStationId}
            onClick={openStationCard}
            readonlyMode={!hasPermissionEditStation}
          />
          {hasPermissionCreateStation && (
            <ButtonAddStation
              callbackFn={selectStationFromDrawer}
              newStationDraft={RIDE_ADD_STATION_NEW_STATION}
            />
          )}

          <div className="add-station__add-new-station">
            <MuiButton
              muiVariant="text"
              onClick={openQuickStationForm}
              disabled={!stationsDropdownListFiltered?.length}
            >
              {t(`general.ADD_A_NEW_ENTITY`, { entity: t(`station.single`) })} +
            </MuiButton>
          </div>

          {showQuickStationForm && (
            <RideAddQuickStationForm
              onClose={handleQuickStationClose}
              onSave={handleQuickStationSave}
            />
          )}

          <div className="add-station__form-actions">
            <MuiButton onClick={closeExpanded} muiVariant="text">
              {t("general.CANCEL")}
            </MuiButton>

            <ButtonAddStationToFixedRide
              onClick={handleStationAdd}
              watchedId={watchedStationId}
            />
          </div>
        </AddStationWrapperStyled>
      )}
      <TableLoadingIndicator loading={isStationsDataLoading} />
    </FormProvider>
  );
};
